import React from 'react';
import { Card, Input, Space, Button, message, Radio } from 'antd';
import CryptoJS from 'crypto-js';

const { TextArea } = Input;

const AESTool: React.FC = () => {
  const [plaintext, setPlaintext] = React.useState('');
  const [ciphertext, setCiphertext] = React.useState('');
  const [key, setKey] = React.useState('');
  const [iv, setIv] = React.useState('');
  const [mode, setMode] = React.useState<'CBC' | 'ECB'>('CBC');

  // AES加密
  const encrypt = () => {
    if (!plaintext.trim()) {
      message.error('请输入要加密的文本');
      return;
    }
    if (!key.trim()) {
      message.error('请输入密钥');
      return;
    }
    if (mode === 'CBC' && !iv.trim()) {
      message.error('CBC模式需要输入IV');
      return;
    }

    try {
      let encrypted;
      if (mode === 'CBC') {
        encrypted = CryptoJS.AES.encrypt(plaintext, key, {
          iv: CryptoJS.enc.Utf8.parse(iv),
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
        }).toString();
      } else {
        encrypted = CryptoJS.AES.encrypt(plaintext, key, {
          mode: CryptoJS.mode.ECB,
          padding: CryptoJS.pad.Pkcs7
        }).toString();
      }
      setCiphertext(encrypted);
      message.success('加密成功');
    } catch (error) {
      message.error('加密失败');
    }
  };

  // AES解密
  const decrypt = () => {
    if (!ciphertext.trim()) {
      message.error('请输入要解密的密文');
      return;
    }
    if (!key.trim()) {
      message.error('请输入密钥');
      return;
    }
    if (mode === 'CBC' && !iv.trim()) {
      message.error('CBC模式需要输入IV');
      return;
    }

    try {
      let decrypted;
      if (mode === 'CBC') {
        decrypted = CryptoJS.AES.decrypt(ciphertext.trim(), key, {
          iv: CryptoJS.enc.Utf8.parse(iv),
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7
        }).toString(CryptoJS.enc.Utf8);
      } else {
        decrypted = CryptoJS.AES.decrypt(ciphertext.trim(), key, {
          mode: CryptoJS.mode.ECB,
          padding: CryptoJS.pad.Pkcs7
        }).toString(CryptoJS.enc.Utf8);
      }
      
      if (!decrypted) {
        throw new Error('解密失败');
      }
      
      setPlaintext(decrypted);
      message.success('解密成功');
    } catch (error) {
      message.error('解密失败，请检查密钥、IV和密文是否正确');
    }
  };

  const generateRandomKey = () => {
    const randomKey = CryptoJS.lib.WordArray.random(256/8).toString();
    setKey(randomKey);
    message.success('已生成随机密钥');
  };

  const generateRandomIV = () => {
    const randomIV = CryptoJS.lib.WordArray.random(128/8).toString();
    setIv(randomIV);
    message.success('已生成随机IV');
  };

  const copyPlaintext = () => {
    if (plaintext) {
      navigator.clipboard.writeText(plaintext);
      message.success('已复制明文到剪贴板');
    }
  };

  const copyCiphertext = () => {
    if (ciphertext) {
      navigator.clipboard.writeText(ciphertext);
      message.success('已复制密文到剪贴板');
    }
  };

  return (
    <Card title="AES 加解密" className="tool-container-responsive">
      <Space direction="vertical" style={{ width: '100%' }}>
        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>加密模式</div>
          <Radio.Group value={mode} onChange={e => setMode(e.target.value)}>
            <Radio.Button value="CBC">CBC</Radio.Button>
            <Radio.Button value="ECB">ECB</Radio.Button>
          </Radio.Group>
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>密钥</div>
          <Input
            placeholder="输入密钥"
            value={key}
            onChange={e => setKey(e.target.value)}
            addonAfter={
              <Button type="text" size="small" onClick={generateRandomKey}>
                随机生成
              </Button>
            }
          />
        </div>

        {mode === 'CBC' && (
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>初始向量 (IV)</div>
            <Input
              placeholder="输入IV"
              value={iv}
              onChange={e => setIv(e.target.value)}
              addonAfter={
                <Button type="text" size="small" onClick={generateRandomIV}>
                  随机生成
                </Button>
              }
            />
          </div>
        )}

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>明文</div>
          <TextArea
            placeholder="输入要加密的文本"
            value={plaintext}
            onChange={e => setPlaintext(e.target.value)}
            rows={4}
          />
          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <Space>
              <Button onClick={copyPlaintext} disabled={!plaintext}>
                复制明文
              </Button>
              <Button type="primary" onClick={encrypt}>
                加密
              </Button>
            </Space>
          </div>
        </div>

        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>密文</div>
          <TextArea
            placeholder="输入要解密的密文"
            value={ciphertext}
            onChange={e => setCiphertext(e.target.value)}
            rows={4}
          />
          <div style={{ textAlign: 'right', marginTop: 8 }}>
            <Space>
              <Button onClick={copyCiphertext} disabled={!ciphertext}>
                复制密文
              </Button>
              <Button type="primary" onClick={decrypt}>
                解密
              </Button>
            </Space>
          </div>
        </div>
      </Space>
    </Card>
  );
};

export default AESTool; 